import { useQuery } from '@tanstack/react-query';
import {
  loadViewDataQueryFn,
  type ViewDataParams,
} from 'shared/components/CustomTable/hooks/loadViewData';
import {
  type ApiError,
  addResourceNameToQueryResult,
  useLegacyAxiosInstance,
} from 'shared/hooks/utils';

export type GetInvoicesViewParams = ViewDataParams & { view_id: string };

export type InvoicesView = Awaited<ReturnType<typeof loadViewDataQueryFn>>;

// Per-resource wrapper over the generic views endpoint (loadViewDataQueryFn).
// Replaces the redux-saga `getView` read for the invoices list. Forward-compatible:
// when the backend exposes a dedicated `GET /invoices` list endpoint, only the
// queryFn here changes — the consumer keeps the same hook contract.
const emptyInvoices: InvoicesView = { data: [], metadata: undefined };

export function useGetInvoicesView(params: GetInvoicesViewParams) {
  const axiosInstance = useLegacyAxiosInstance();
  const { view_id, ...filters } = params;

  const queryResult = useQuery<InvoicesView, ApiError>({
    queryKey: ['invoices-view', params],
    queryFn: () => loadViewDataQueryFn(axiosInstance, view_id, filters),
    enabled: Boolean(view_id),
    placeholderData: previousData => previousData ?? emptyInvoices,
  });

  return addResourceNameToQueryResult<InvoicesView, ApiError, 'invoices', 'PreventUndefined'>(
    'invoices',
    queryResult,
    emptyInvoices,
  );
}
